/**
 * Módulo 3 del tutorial: Cargar tus animales (KAN-232).
 *
 * En MiCampoDigital los animales se cargan por SESIONES: una sesión junta
 * todo lo visto en un día de trabajo (mangrullo, balanza, caravanas). El
 * módulo guía: Mi Ganado → Nueva Sesión → elegir campo(s) → cargar el primer
 * animal (caravana, categoría, peso) → guardar.
 *
 * Anclas: menu-animals, qa-animals-new, field-selector-start,
 * form-animal-caravana, form-animal-categoria, btn-save-animal.
 */

import type { TutorialModuleDef } from '../../../services/tutorial/types';
import { getRepositories } from '../../../services/storage';

export const animalesModule: TutorialModuleDef = {
  id: 'animales',
  title: 'Cargar tus animales',
  description: 'Cargá tu primer animal por sesión: caravana, categoría y peso. Podés conectar balanza y lector RFID.',
  emoji: '🐄',
  image: 'images/tutorial/animales.png',
  permission: { module: 'animals', action: 'create' },
  grupo: 'esencial',
  route: '/animals',
  steps: [
    {
      id: 'concepto-sesiones',
      route: '/tutorial',
      text: 'Los animales se cargan por SESIONES: cada sesión junta lo trabajado en un día (mangrullo, balanza, caravanas). Así queda el historial prolijo de cada animal.',
      narration:
        'Vamos con lo principal: tus animales. En esta app no se cargan de a uno suelto, sino por sesiones de trabajo. Una sesión es tu día de mangrullo: pesás, caravaneás, y todo lo de esa jornada queda junto, ordenado como el libro de campo de toda la vida. Después, cada animal tiene su historia completa. Empecemos viendo donde viven tus animales.',
    },
    {
      id: 'ir-a-mi-ganado',
      target: '[data-ar-guide="menu-animals"]',
      label: 'Mi Ganado',
      text: 'En el menú, tocá "Mi Ganado" (en celular: abrí el menú ☰).',
      narration:
        'Andá al menú y tocá donde dice Mi Ganado. Ahí vas a ver la lista de todos tus animales, con su categoría, su peso y en qué ubicación está cada uno.',
    },
    {
      id: 'nueva-sesion',
      route: '/animals',
      target: '[data-ar-guide="animals-btn-nueva-sesion"]',
      label: 'Nueva Sesión',
      text: 'En esta barra de acciones, tocá "Nueva Sesión" para arrancar una jornada de carga (también está en Acciones rápidas del menú).',
      narration:
        'Arriba de la lista tenés los botones de acciones. Tocá Nueva Sesión y arranca la jornada de carga. Si preferís, después lo podés hacer directo desde las acciones rápidas del menú, pero de acá es lo más derecho.',
    },
    {
      id: 'elegir-campo',
      route: '/animals/new',
      target: '[data-ar-guide="field-selector-start"]',
      label: 'Elegí los datos y comenzá',
      text: 'Elegí qué datos del animal vas a completar en esta sesión —los demás se ocultan para cargar más rápido— y tocá "Comenzar a cargar". Si querés lo esencial ya marcado, usá "Carga rápida". ¿Se te olvidó alguno? Tranquilo: después lo agregás editando el animal.',
      narration:
        'Antes de arrancar, decile a la app qué datos del animal vas a anotar en esta jornada: la caravana, la categoría, el peso, lo que necesites. Los que no marques quedan escondidos, así el formulario queda cortito y cargás más rápido, sin tanto tecleo. Si querés ir derecho al asunto, tocá Carga rápida, que ya trae lo esencial marcado. Y si después te das cuenta de que te faltó un dato, no le des vueltas: más adelante en este mismo tutorial te muestro cómo agregarlo. Cuando estés conforme, apretá Comenzar a cargar.',
    },
    {
      id: 'caravana',
      route: '/animals/new',
      target: '[data-ar-guide="form-animal-caravana"]',
      advanceOnClick: false,
      label: 'Caravana',
      text: 'Cargá el primer animal: su caravana (número de identificación). Si tenés lector RFID o balanza conectada, se completa solo.',
      narration:
        'Vamos a cargar el primer animal de verdad. Lo primero es la caravana, que es la identificación del bicho. Ponela tal cual la ves. Y si tenés lector de chip o balanza conectada, la app la carga sola: vos solo revisá que esté todo bien.',
    },
    {
      id: 'categoria',
      route: '/animals/new',
      target: '[data-ar-guide="form-animal-categoria"]',
      advanceOnClick: false,
      label: 'Categoría',
      text: 'Elegí la categoría: ternero, vaquillona, vaca, novillo… La app la usa para todos los análisis.',
      narration:
        'Ahora la categoría: si es ternero, vaquillona, vaca o novillo, según lo que sea el animal. Con eso la app hace su magia después: te arma los análisis, las curvas de peso y las cuentas de la hacienda por categoría.',
    },
    {
      id: 'agregar-campo',
      route: '/animals/new',
      target: '[data-ar-guide="animal-add-campo"]',
      label: '+ Agregar campo',
      advanceOnClick: false,
      text: '¿Necesitás un dato que no elegiste al arranque? Con "+ Agregar campo" sumás cualquiera de los que faltan al formulario, sin salir de la sesión.',
      narration:
        'Fijate en este botón que dice Más Agregar campo. Si te das cuenta de que necesitás un dato que no marcaste al arrancar —por ejemplo la madre, o si es comprado— no hay problema: tocá acá, elegí el dato de la lista, y el formulario lo suma al toque. Así vas armando la carga a tu medida, sin empezar de cero.',
    },
    {
      id: 'guardar-animal',
      route: '/animals/new',
      target: '[data-ar-guide="btn-save-animal"]',
      label: 'Guardar animal',
      text: 'Tocá guardar y ¡listo! Tu primer animal quedó en el sistema. ¿Te faltó algún dato? Buscalo en Mi Ganado, tocá el lápiz de Editar y agregalo: ahí el formulario muestra todos los campos.',
      narration:
        'Buen trabajo. Apretá guardar y ese animal ya queda en el sistema, con su historia arrancando de cero. Y una cosa más para que sepas: si te quedó algún dato pendiente, buscás el animal en Mi Ganado, le das al lápiz de Editar y le cargás lo que falte — ahí el formulario te muestra todos los campos, no solo los de hoy. Los demás animales son igual: caravana, categoría, y a veces el peso de la balanza. Antes de que te des cuenta, tenés todo el rodeo adentro.',
    },
    {
      id: 'animales-sesion',
      route: '/animals/new',
      target: '[data-ar-guide="session-animals-panel"]',
      label: 'Tabla de la sesión',
      advanceOnClick: false,
      text: 'Mirá arriba: cada animal que cargás se va sumando a la tabla de la sesión. Ahí mismo podés corregir cualquier dato, y seguir cargando más animales hasta que toques "Salir" para cerrar la sesión.',
      narration:
        '¿Ves la tabla que te apareció arriba del formulario? Ahí van cayendo los animales a medida que los cargás, como las cuentas en la libreta al pie del mangrullo. Si te equivocaste en algo, lo corregís ahí mismo, tocando el dato. Y la sesión no se cierra sola: podés cargar todo el día si hace falta; cuando termines, apretás Salir y la jornada queda cerrada, ordenadita en su sesión.',
    },
    {
      id: 'ir-a-sesiones',
      target: '[data-ar-guide="menu-sesiones"]',
      label: 'Sesiones de Carga',
      text: 'En el menú lateral, tocá "Sesiones de Carga" (en celular: menú ☰, sección Ganado): ahí viven todas tus jornadas de carga.',
      narration:
        'Ahora te llevo a donde viven todas tus jornadas. Mirá el menú del costado, en la sección Ganado, y tocá donde dice Sesiones de Carga. Si estás desde el celular, abrí el menú con las rayitas de arriba. Ahí van quedando cada una de tus cargas, agrupadas por día, como el libro de campo.',
    },
    {
      id: 'sesion-seccion',
      route: '/sesiones',
      target: '[data-ar-guide="sesion-card"]',
      label: 'Tu sesión de hoy',
      advanceOnClick: false,
      text: 'Esta card es tu sesión de hoy, con sus animales y su fecha. Tocando la sesión la abrís y revisás todo lo que se cargó.',
      narration:
        '¿Ves esta tarjeta? Es tu sesión de hoy, con su fecha y los animales que venís cargando. Tocando la sesión la abrís y revisás todo lo que se cargó, animal por animal. Es tu libro de campo digital, ordenadito por jornada.',
    },
    {
      id: 'volver-mi-ganado',
      target: '[data-ar-guide="menu-animals"]',
      label: 'Mi Ganado',
      text: 'Ahora volvamos a la lista completa: en el menú, tocá "Mi Ganado" (sección Ganado).',
      narration:
        'Ahora volvamos a ver la lista completa de tu hacienda. Tocá otra vez Mi Ganado en el menú, ahí en la sección Ganado, que ya lo conocés del arranque. En un segundo estamos.',
    },
    {
      id: 'editar-despues',
      route: '/animals',
      target: '[data-ar-guide="animals-row-edit"]',
      label: 'Lápiz de Editar',
      advanceOnClick: false,
      text: 'Esta es la lista de Mi Ganado: cada animal tiene su lápiz de Editar. Ahí completás cualquier dato que te haya quedado pendiente — el formulario de edición muestra todos los campos.',
      narration:
        'Y acá te lo muestro, como te prometí: esta es la lista de Mi Ganado. Fijate que cada animal tiene su lapicero al costado, el botón de Editar. Si te quedó pendiente algún dato, le das ahí y te aparece el formulario completo, con todos los campos, no solo los de la carga de hoy: le agregás lo que falte y queda todo al día. Con esto ya sabés cargar tus animales y dejarlos al tanto. Los demás módulos te esperan cuando quieras.',
    },
  ],
  completionCheck: async () => {
    const animales = await getRepositories().animal.getAllForActiveEmpresa();
    return animales.length > 0;
  },
};
